import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as labourSepPaymentAPI from '../../api/HRAPI/labourSepPaymentAPI';

// Async Thunks
// =============================================

export const fetchConsolidateNoForLBSep = createAsyncThunk(
    'labourSepPayment/fetchConsolidateNoForLBSep',
    async (params, { rejectWithValue }) => {
        try {
            const response = await labourSepPaymentAPI.getConsolidateNoForLBSep(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Consolidate Numbers');
        }
    }
);

export const fetchLBCostcenterPaidAmount = createAsyncThunk(
    'labourSepPayment/fetchLBCostcenterPaidAmount',
    async (params, { rejectWithValue }) => {
        try {
            const response = await labourSepPaymentAPI.getLBCostcenterPaidAmount(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Cost Center Paid Amount');
        }
    }
);

export const fetchCompanyBankDetails = createAsyncThunk(
    'labourSepPayment/fetchCompanyBankDetails',
    async (_, { rejectWithValue }) => {
        try {
            const response = await labourSepPaymentAPI.getCompanyBankDetails();
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Bank Details');
        }
    }
);

export const saveLBSEPPayment = createAsyncThunk(
    'labourSepPayment/saveLBSEPPayment',
    async (params, { rejectWithValue }) => {
        try {
            const response = await labourSepPaymentAPI.saveLBSEPPayment(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to save SEP Payment');
        }
    }
);

// Initial State
// =============
const initialState = {
    consolidateNumbers: [],
    paidData: null, // { PaymentExistStatus, PaidDataList, ... }
    bankList: [],
    saveResult: null,
    saveStatus: null, // null | 'pending' | 'success' | 'failed'

    loading: {
        consolidateNumbers: false,
        paidData: false,
        bankList: false,
        save: false,
    },

    errors: {
        consolidateNumbers: null,
        paidData: null,
        bankList: null,
        save: null,
    },
};

const labourSepPaymentSlice = createSlice({
    name: 'labourSepPayment',
    initialState,
    reducers: {
        clearConsolidateNumbers: (state) => {
            state.consolidateNumbers = [];
            state.errors.consolidateNumbers = null;
        },
        clearPaidData: (state) => {
            state.paidData = null;
            state.errors.paidData = null;
        },
        clearSaveResult: (state) => {
            state.saveResult = null;
            state.saveStatus = null;
            state.errors.save = null;
        },
        resetLabourSepPayment: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchConsolidateNoForLBSep.pending, (state) => {
                state.loading.consolidateNumbers = true;
                state.errors.consolidateNumbers = null;
            })
            .addCase(fetchConsolidateNoForLBSep.fulfilled, (state, action) => {
                state.loading.consolidateNumbers = false;
                state.consolidateNumbers = action.payload?.Data || [];
            })
            .addCase(fetchConsolidateNoForLBSep.rejected, (state, action) => {
                state.loading.consolidateNumbers = false;
                state.errors.consolidateNumbers = action.payload;
                state.consolidateNumbers = [];
            });

        builder
            .addCase(fetchLBCostcenterPaidAmount.pending, (state) => {
                state.loading.paidData = true;
                state.errors.paidData = null;
            })
            .addCase(fetchLBCostcenterPaidAmount.fulfilled, (state, action) => {
                state.loading.paidData = false;
                state.paidData = action.payload?.Data || null;
            })
            .addCase(fetchLBCostcenterPaidAmount.rejected, (state, action) => {
                state.loading.paidData = false;
                state.errors.paidData = action.payload;
                state.paidData = null;
            });

        builder
            .addCase(fetchCompanyBankDetails.pending, (state) => {
                state.loading.bankList = true;
                state.errors.bankList = null;
            })
            .addCase(fetchCompanyBankDetails.fulfilled, (state, action) => {
                state.loading.bankList = false;
                state.bankList = action.payload?.Data || [];
            })
            .addCase(fetchCompanyBankDetails.rejected, (state, action) => {
                state.loading.bankList = false;
                state.errors.bankList = action.payload;
                state.bankList = [];
            });

        builder
            .addCase(saveLBSEPPayment.pending, (state) => {
                state.loading.save = true;
                state.errors.save = null;
                state.saveStatus = 'pending';
            })
            .addCase(saveLBSEPPayment.fulfilled, (state, action) => {
                state.loading.save = false;
                state.saveResult = action.payload;

                const responseStr = typeof action.payload?.Data === 'string'
                    ? action.payload.Data
                    : (typeof action.payload === 'string' ? action.payload : '');

                const successMessage = responseStr.split('$')[0];

                if (successMessage === 'Submited') {
                    state.saveStatus = 'success';
                } else {
                    state.saveStatus = 'failed';
                    state.errors.save = responseStr || 'Save failed';
                }
            })
            .addCase(saveLBSEPPayment.rejected, (state, action) => {
                state.loading.save = false;
                state.errors.save = action.payload;
                state.saveStatus = 'failed';
                state.saveResult = null;
            });
    },
});

export const {
    clearConsolidateNumbers,
    clearPaidData,
    clearSaveResult,
    resetLabourSepPayment,
} = labourSepPaymentSlice.actions;

// Selectors
// =========
export const selectConsolidateNumbersArray = (state) => {
    const nos = state.labourSepPayment.consolidateNumbers;
    return Array.isArray(nos) ? nos : [];
};
export const selectConsolidateNumbersLoading = (state) => state.labourSepPayment.loading.consolidateNumbers;

export const selectPaidData = (state) => state.labourSepPayment.paidData;
export const selectPaidDataLoading = (state) => state.labourSepPayment.loading.paidData;

export const selectBankListArray = (state) => {
    const banks = state.labourSepPayment.bankList;
    return Array.isArray(banks) ? banks : [];
};
export const selectBankListLoading = (state) => state.labourSepPayment.loading.bankList;

export const selectSaveStatus = (state) => state.labourSepPayment.saveStatus;
export const selectSaveLoading = (state) => state.labourSepPayment.loading.save;
export const selectSaveError = (state) => state.labourSepPayment.errors.save;

export default labourSepPaymentSlice.reducer;
